<template>
    <div class="login-page">
        <div class="login-card">
            <div class="login-card__logo">
                <img src="../../img/id-plein.png" alt="Logo">
            </div>

            <p class="login-card__welcome">{{ uLang('app.login_welcome') }}</p>
            <p class="login-card__intro">{{ uLang('app.login_2fa_intro') }}</p>

            <form class="login-card__form" @submit.prevent="onSubmit" @keydown="form.errors.clear($event.target.name)">
                <div class="form-group" v-bind:class="{ 'has-error': form.errors.has('email') }">
                    <label for="email">{{ uLang('app.email_cm') }}</label>
                    <input type="text" class="form-control" id="email" name="email" v-model="form.email" required>
                    <span class="help-block" v-if="form.errors.has('email')" v-text="form.errors.get('email')"></span>
                </div>

                <div class="form-group" v-bind:class="{ 'has-error': form.errors.has('password') }">
                    <label for="password">{{ uLang('app.password') }}</label>
                    <input type="password" class="form-control" id="password" name="password" v-model="form.password" required>
                    <span class="help-block" v-if="form.errors.has('password')" v-text="form.errors.get('password')"></span>
                </div>

                <button type="submit" :disabled="form.busy" class="btn btn-brand btn-block login-card__btn">
                    {{ uLang('app.sign_in') }}
                </button>

                <div class="login-card__divider">
                    <span>{{ uLang('app.login_or') }}</span>
                </div>

                <!--
                    Host for Google's own button. It stays hidden: the link below is the button people see.
                    mounted() renders Google's button into this div, and that link clicks it.
                    Google only completes sign-in from its own button.
                    Do not add id="g_id_onload" or class="g_id_signin". The Google script treats those as a
                    second setup and calls initialize() again, which cancels the prompt started in mounted().
                -->
                <div ref="googleSignInButton" style="display:none"></div>

                <a @click="onGoogleSignInClick" id="custom-google-btn" class="btn btn-brand btn-block login-card__btn login-card__btn--google">
                    <svg class="google-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 48 48">
                        <path fill="#EA4335" d="M24 9.5c3.36 0 6.41 1.23 8.79 3.25l6.57-6.57C34.31 2.64 29.47 0 24 0 14.77 0 6.99 4.83 2.71 11.81l7.92 6.09C13.1 12.58 18.3 9.5 24 9.5z"></path>
                        <path fill="#34A853" d="M46.62 24.5c0-1.53-.14-3.02-.41-4.45H24v8.64h12.7c-.55 2.88-2.16 5.33-4.59 6.97l7.09 5.44C43.23 36.17 46.62 30.91 46.62 24.5z"></path>
                        <path fill="#4A90E2" d="M24 48c6.29 0 11.57-2.08 15.41-5.66l-7.09-5.44c-2.02 1.35-4.61 2.14-8.32 2.14-6.42 0-11.87-4.35-13.82-10.32l-8.06 6.16C6.01 42.68 14.53 48 24 48z"></path>
                        <path fill="#FBBC05" d="M10.18 28.72A14.78 14.78 0 0 1 9.5 24c0-1.63.28-3.2.77-4.72l-8.06-6.16A23.963 23.963 0 0 0 0 24c0 3.95.93 7.67 2.71 11.81l7.92-6.09c-.31-1.21-.45-2.49-.45-3.81z"></path>
                    </svg>
                    <span>{{ uLang('app.sign_in_with_google') }}</span>
                </a>

                <router-link :to="{ name: 'password.reset' }" class="login-card__forgot">
                    {{ uLang('app.forgot_password') }}
                </router-link>
            </form>
        </div>
    </div>
</template>
<script>
import Form from '~/Form/Form'
import { SuccessAlert, ErrorAlert } from "~/Swals";

export default {
    name: "login",
    data() {
        return {
            form: new Form({
                email: '',
                password: '',
            }, true),
            googleClientId: window.Laravel.googleClientId,
            googleAttemptId: null,
        }
    },
    methods: {
        createGoogleAttemptId() {
            if (window.crypto && typeof crypto.randomUUID === 'function') {
                return crypto.randomUUID();
            }

            return Date.now().toString(16) + Math.random().toString(16).slice(2);
        },
        postGoogleLoginLog(routeName, payload = {}) {
            return fetch(route(routeName), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify(Object.assign({
                    attempt_id: this.googleAttemptId,
                }, payload)),
                keepalive: true,
            }).catch((error) => {
                console.error('Google login log failed:', error);
            });
        },
        // The visible link only forwards the click to Google's rendered button in the hidden div.
        onGoogleSignInClick() {
            this.googleAttemptId = this.createGoogleAttemptId();
            this.postGoogleLoginLog('auth.google.started');

            const googleSignInButton = this.$refs.googleSignInButton;
            if (googleSignInButton) {
                const innerButton = googleSignInButton.querySelector('div[role="button"]');
                if (innerButton) {
                    innerButton.click();
                } else {
                    this.postGoogleLoginLog('auth.google.client-event', {
                        event: 'button_missing',
                        message: 'No inner button with role="button" found',
                    });
                    console.warn('No inner button with role="button" found inside the googleSignInButton');
                }
            } else {
                this.postGoogleLoginLog('auth.google.client-event', {
                    event: 'button_missing',
                    message: 'No googleSignInButton reference found',
                });
                console.warn('No googleSignInButton reference found');
            }
        },
        // The script in app.blade.php is async, so it can still be downloading when this page mounts.
        googleSignInReady() {
            return !!(window.google && window.google.accounts && window.google.accounts.id);
        },
        scheduleGoogleSignIn() {
            if (this.googleSignInReady()) {
                this.initializeGoogleSignIn();
                return;
            }

            const startedAt = Date.now();
            this.googleSignInTimer = setInterval(() => {
                if (this.googleSignInReady()) {
                    this.cancelGoogleSignInSchedule();
                    this.initializeGoogleSignIn();
                    return;
                }

                if (Date.now() - startedAt > 10000) {
                    this.cancelGoogleSignInSchedule();
                    console.warn('Google Sign-In script did not load');
                }
            }, 50);
        },
        cancelGoogleSignInSchedule() {
            if (this.googleSignInTimer) {
                clearInterval(this.googleSignInTimer);
                this.googleSignInTimer = null;
            }
        },
        // One setup for both ways in, after the Google script has loaded.
        // 1. initialize() stores the client id and the callback shared by the button and the automatic prompt.
        // 2. renderButton() draws Google's button into the hidden div. The visible link clicks that button.
        // 3. prompt() asks Chrome to offer Google sign-in without a click. Chrome can still suppress it.
        initializeGoogleSignIn() {
            if (!this.googleSignInReady()) {
                return;
            }

            window.google.accounts.id.initialize({
                client_id: this.googleClientId,
                callback: this.onGoogleSignIn,
                auto_select: false,
                itp_support: true,
            });
            window.google.accounts.id.renderButton(
                this.$refs.googleSignInButton,
                {
                    theme: 'outline',
                    size: 'medium',
                    width: 290,
                    type: 'standard'
                }
            );
            window.google.accounts.id.prompt();
        },
        onSubmit() {
            this.form.post('/api/login')
              .then(async (data) => {
                if (data.requires_2fa) {
                    await this.$store.dispatch('resetUser');

                    sessionStorage.setItem('login_otp', JSON.stringify({
                        masked_email: data.masked_email,
                        expires_in: data.expires_in,
                        resend_available_in: data.resend_available_in,
                        resends_remaining: data.resends_remaining,
                    }));

                    this.$router.push({
                        name: 'login.verify',
                        query: { token: data.challenge_token },
                    });

                    return;
                }

                this.$store.dispatch('saveToken', {
                  token: data.token,
                  remember: this.remember
                });

                await this.$store.dispatch('fetchUser');

                this.$router.push({
                    name: 'dashboard'
                })
              })
              .catch(response => this.form.password = '');
        },
        async onGoogleSignIn(response) {
            const token = response && response.credential;
            try
            {
                await this.postGoogleLoginLog('auth.google.client-event', {
                    event: 'google_responded',
                    has_credential: !!token,
                });

                const result = await fetch(route('auth.google.callback'), {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                    },
                    body: JSON.stringify({
                        token,
                        attempt_id: this.googleAttemptId,
                    })
                });

                const data = await result.json();

                if (data.token) {
                    this.$store.dispatch('saveToken', {
                        token: data.token,
                        remember: false
                    });
                    await this.$store.dispatch('fetchUser');
                    this.$router.push({
                        name: 'dashboard'
                    })
                } else {
                    return new ErrorAlert({
                        text: "Login Failed",
                    }).show();
                }
            } catch (error) {
                console.error('Error during login:', error);
                this.postGoogleLoginLog('auth.google.client-event', {
                    event: 'client_exception',
                    message: error && error.message ? error.message : String(error),
                });
            }
        }
    },
    mounted: function() {
        this.scheduleGoogleSignIn();
    },
    beforeDestroy: function() {
        this.cancelGoogleSignInSchedule();
    },
}
</script>
<style lang="scss" scoped>
.login-page {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    padding: 24px 16px;
    background: linear-gradient(180deg, #eef3f8 0%, #e4eaf2 100%);
    font-family: 'Roboto', sans-serif;
    letter-spacing: normal;
}

.login-card {
    width: 100%;
    max-width: 440px;
    padding: 40px 36px 32px;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
    color: #333;
    letter-spacing: normal;
}

.login-card__logo {
    text-align: center;
    margin-bottom: 28px;

    img {
        max-width: 150px;
        height: auto;
    }
}

.login-card__welcome {
    margin: 0 0 10px;
    font-size: 18px;
    font-weight: 700;
    line-height: 1.3;
    color: #333;
    text-align: left;
    letter-spacing: normal;
}

.login-card__intro {
    margin: 0 0 28px;
    font-size: 15px;
    font-weight: 400;
    line-height: 1.6;
    color: #333;
    text-align: left;
    letter-spacing: normal;
}

.login-card__form {
    .form-group {
        margin-bottom: 18px;
    }

    label {
        display: block;
        margin-bottom: 8px;
        font-size: 15px;
        font-weight: 700;
        color: #333;
        letter-spacing: normal;
    }

    .form-control {
        height: 42px;
        padding: 8px 12px;
        font-size: 15px;
        color: #333;
        border: 1px solid #ccc;
        border-radius: 4px;
        box-shadow: none;
        letter-spacing: normal;

        &:focus {
            border-color: #7a6aaa;
            box-shadow: 0 0 0 3px rgba(90, 69, 138, 0.12);
        }
    }
}

.login-card__btn {
    height: 42px;
    margin-top: 6px;
    font-size: 15px;
    font-weight: 700;
    border-radius: 4px;
    letter-spacing: normal;
}

.login-card__divider {
    display: flex;
    align-items: center;
    margin: 18px 0;
    color: #666;
    font-size: 12px;
    font-weight: 400;
    text-transform: uppercase;
    letter-spacing: normal;

    &::before,
    &::after {
        content: '';
        flex: 1;
        height: 1px;
        background: #ccc;
    }

    span {
        padding: 0 12px;
    }
}

#custom-google-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    text-decoration: none;
    cursor: pointer;
}

.google-icon {
    width: 20px;
    height: 20px;
    margin-right: 10px;
    flex-shrink: 0;
}

.login-card__forgot {
    display: block;
    margin-top: 24px;
    font-size: 15px;
    font-weight: 400;
    color: #C10F6D;
    text-align: center;
    text-decoration: none;
    letter-spacing: normal;

    &:hover {
        color: #a00d5a;
        text-decoration: none;
    }
}

.has-error {
    color: #d32f2f;

    .form-control {
        border-color: #d32f2f;
    }

    .help-block {
        margin-top: 4px;
        font-size: 13px;
    }
}
</style>
